import React, { useState } from 'react'
import { submitExpense } from '../../api/expenses'

interface Props {
  jobId: string
  onSubmitted?: () => void
  onCancel?: () => void
}

/** Compact expense form for job completion (reuses job_expenses submit API). */
export function JobExpenseQuickForm({ jobId, onSubmitted, onCancel }: Props) {
  const [description, setDescription] = useState('')
  const [quantity, setQuantity] = useState('1')
  const [paidOutOfPocket, setPaidOutOfPocket] = useState(false)
  const [amount, setAmount] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedNote, setSavedNote] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!description.trim()) return
    const qty = parseFloat(quantity)
    if (Number.isNaN(qty) || qty <= 0) {
      setError('Quantity must be a positive number')
      return
    }
    let parsedAmount: number | null = null
    if (paidOutOfPocket) {
      parsedAmount = parseFloat(amount)
      if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
        setError('Enter a valid amount when paid out of pocket')
        return
      }
    }
    setSubmitting(true)
    setError(null)
    try {
      await submitExpense({
        job_id: jobId,
        description: description.trim(),
        quantity: qty,
        paid_out_of_pocket: paidOutOfPocket,
        amount: parsedAmount,
      })
      setDescription('')
      setQuantity('1')
      setPaidOutOfPocket(false)
      setAmount('')
      setSavedNote('Expense submitted')
      onSubmitted?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit expense')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-3 space-y-3 mb-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium text-gray-900">Enter expenses</p>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="text-xs text-gray-500 hover:text-gray-800"
          >
            Close
          </button>
        )}
      </div>
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
          {error}
        </p>
      )}
      {savedNote && !error && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5">
          {savedNote}
        </p>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <input
          type="text"
          required
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          placeholder="e.g. Extra bags, supplies"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700">Quantity</label>
          <input
            type="number"
            min="0.01"
            step="any"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div className="flex items-end pb-1">
          <label className="flex items-center gap-2 text-sm text-gray-700 min-h-[40px]">
            <input
              type="checkbox"
              checked={paidOutOfPocket}
              onChange={(e) => setPaidOutOfPocket(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Out of pocket
          </label>
        </div>
      </div>
      {paidOutOfPocket && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Amount ($)</label>
          <input
            type="number"
            min="0.01"
            step="0.01"
            required
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        className="w-full px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
      >
        {submitting ? 'Submitting…' : 'Submit expense'}
      </button>
    </form>
  )
}
